import { Helper, MeasurementCodeType, NeuteredCodeEnum, Programs, Tool, VisitType } from '@app/shared/utils/';
import { Consultation, Patient, Pet, PetInfo } from '@app/core/models';
import { PetInteraction } from '@app/core/services/tracking';
import { format } from 'date-fns';

export const gtmWeightProgram = (program: Programs): string => {
  switch (program) {
    case Programs.WEIGHT_LOSS:
      return 'Weight loss program';
    case Programs.WEIGHT_STABILISATION_STEP_1:
      return 'After weight loss program';
    case Programs.WEIGHT_STABILISATION_STEP_2:
      return 'After weight loss program';
    default:
      return 'Weight maintenance program';
  }
};

export const gtmVisitType = (tool: Tool): VisitType => {
  return {
    [Tool.WeightManagement]: VisitType.WeightManagement,
    [Tool.SmartReco]: VisitType.SmartReco,
    [Tool.RapidReco]: VisitType.RapidReco,
    [Tool.RenalDetect]: VisitType.RenalDetect,
    [Tool.Rationing]: VisitType.RationingTool,
  }[tool];
};

export const gtmPetInfoToPetInteraction = (
  patient: Patient | null,
  lastConsultation: Consultation = null,
  currentBigMeasurementUnit: MeasurementCodeType,
  petInfo?: PetInfo,
): PetInteraction => {
  const pet = patient?.pet || {};
  let weight: string;
  let weightUnit: MeasurementCodeType;

  if (petInfo) {
    weight = petInfo.weight?.toString();
    weightUnit = getValueOrUndefined(currentBigMeasurementUnit);
  } else {
    weight = patient?.pet?.weight?.measure?.toString() ?? lastConsultation?.visit?.weight?.measure?.toString();
    weightUnit = getValueOrUndefined(patient?.pet?.weight?.measureUnit ?? lastConsultation?.visit?.weight?.measureUnit);
  }

  const BCS = pet.idealBodyWeight?.bcs?.toString() ?? petInfo?.bcs?.toString();

  let age: string;
  if (pet.birth) {
    age = Helper._petAge(pet);
  } else if (petInfo?.birthdate) {
    age = Helper._petAge({ birth: { date: petInfo.birthdate } } as Pet);
  } else {
    age = undefined;
  }

  let neutered: NeuteredCodeEnum;
  if (typeof pet.neutered === 'boolean' || typeof petInfo?.neutered === 'boolean') {
    neutered = isNeutered(pet.neutered ?? petInfo.neutered);
  }

  return {
    newPet: !patient,
    newPetOwner: !patient,
    id: getValueOrUndefined(pet.id),
    BCS,
    activityLevel: pet.petActivityCode ?? pet.petActivity ?? petInfo?.petActivity,
    breed: getValueOrUndefined(pet.breedCode ?? petInfo?.breedObject?.breedCode),
    gender: getValueOrUndefined(pet.genderCode ?? petInfo?.gender),
    birthdate: formatBirthdate(pet?.birth?.date ?? petInfo?.birthdate),
    weight,
    weightUnit,
    age,
    name: getValueOrUndefined(pet.name ?? petInfo?.name),
    specie: getValueOrUndefined(pet.speciesCode ?? petInfo?.speciesCode),
    neutered,
    lifestage: getValueOrUndefined(pet.lifeStage ?? petInfo?.lifestage),
    reproductionStatus: pet.reproductionStatusCode ?? petInfo?.reproductionStatus ?? undefined,
    expectedWeight: patient?.consultation?.nextVisit?.expected?.weight?.measure
      ? `${patient.consultation.nextVisit.expected.weight.measure}`
      : undefined,
    ownerId: getValueOrUndefined(pet.ownerId),
    weightProgram: lastConsultation?.visit?.program ? gtmWeightProgram(lastConsultation.visit.program) : undefined,
    pathology: getValueOrUndefined((lastConsultation?.pathologies ?? petInfo?.pathologies)?.join('-')),
    sensitivities: getValueOrUndefined((lastConsultation?.sensitivities ?? petInfo?.sensitivities)?.join('-')),
    neuteredToday: petInfo?.neuteredToday,
  };
};

export function getValueOrUndefined<T>(value: T): T | undefined {
  return value || undefined;
}

export const isNeutered = (neutered: boolean): NeuteredCodeEnum => (neutered ? NeuteredCodeEnum.Neutered : NeuteredCodeEnum.NotNeutered);

export const formatBirthdate = (birthdate: Date): string | undefined => {
  if (!birthdate) {
    return undefined;
  } else {
    return format(birthdate, 'yyyy-MM-dd');
  }
};
